import { useMemo, useState, type ReactNode } from 'react';
import {Table, TBody, Td, Th, THead, Tr} from "@mds/web-ui-table";
import styles from "../TableWithPagination/TableWithPagination.module.scss";

export interface TableColumn<T extends object> {
  Header: ReactNode | (() => ReactNode);
  accessor: keyof T | ((row: T) => unknown);
  Cell?: (props: { value: any; row: T }) => ReactNode;
  id?: string;
}

interface TableWithPaginationProps<T extends object> {
  columns: TableColumn<T>[];
  data: T[];
}

const TableWithPagination = <T extends object>({ columns, data }: TableWithPaginationProps<T>) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const pageCount = Math.max(Math.ceil(data.length / pageSize), 1);
  const pageOptions = Array.from({ length: pageCount }, (_, index) => index);
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < pageCount - 1;
  const page = useMemo(
    () => data.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
    [data, pageIndex, pageSize]
  );

  const gotoPage = (newPageIndex: number) => {
    setPageIndex(Math.min(Math.max(newPageIndex, 0), pageCount - 1));
  };

  const getColumnId = (column: TableColumn<T>, columnIndex: number) => (
    column.id ?? (typeof column.accessor === 'string' ? column.accessor : String(columnIndex))
  );

  const getCellValue = (row: T, column: TableColumn<T>) => (
    typeof column.accessor === 'function' ? column.accessor(row) : row[column.accessor]
  );

  const renderHeader = (column: TableColumn<T>) => (
    typeof column.Header === 'function' ? column.Header() : column.Header
  );

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbers = 5;
    const totalPageCount = pageOptions.length;

    if (totalPageCount <= 1) {
      return null; // Hide pagination if there's only one page
    }

    const startPage = Math.floor(pageIndex / maxPageNumbers) * maxPageNumbers;
    const endPage = Math.min(startPage + maxPageNumbers, totalPageCount);

    for (let i = startPage; i < endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => gotoPage(i)}
          disabled={pageIndex === i}
          className={`${styles['gray-text']}`}
          style={{ fontWeight: pageIndex === i ? 'bold' : 'normal' }}
        >
          {i + 1}
        </button>
      );
    }

    if (endPage < totalPageCount) {
      pageNumbers.push(<span key="ellipsis">...</span>);
      pageNumbers.push(
        <button
          key={totalPageCount - 1}
          onClick={() => gotoPage(totalPageCount - 1)}
          disabled={pageIndex === totalPageCount - 1}
          className={`${styles['gray-text']}`}
          style={{ fontWeight: pageIndex === totalPageCount - 1 ? 'bold' : 'normal' }}
        >
          {totalPageCount}
        </button>
      );
    }

    return pageNumbers;
  };

  return (
      <div className={styles['pagination-table-container']}>
        <div className={styles.tableWithPagination}>
          <Table className={styles['pagination-table']}>
            <THead>
                <Tr>
                  {columns.map((column, columnIndex) => (
                    <Th
                      className={styles['pagination-table-header']}
                      style={{
                        paddingBlock: '15px',
                        borderColor: 'lightgray'
                      }}
                      key={getColumnId(column, columnIndex)}
                    >
                      {renderHeader(column)}
                    </Th>
                  ))}
                </Tr>
            </THead>
            <TBody>
              {page.map((row, rowIndex) => {
                return (
                  <Tr key={pageIndex * pageSize + rowIndex}>
                    {columns.map((column, columnIndex) => {
                      const value = getCellValue(row, column);
                      return (
                        <Td
                          key={`${pageIndex * pageSize + rowIndex}-${getColumnId(column, columnIndex)}`}
                          className={`${styles['padding-10px']} ${styles['pagination-table-row']}`}
                          style={{
                            borderColor: 'lightgray'
                          }}
                        >
                          {column.Cell ? column.Cell({ value, row }) : String(value ?? '')}
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        </div>
        <div className={styles['gray-text']}>
          <span className={styles['padding-10px']}>
            Showing
            <select
              className={`${styles['gray-text']} ${styles['pagination-margin-5px']}`}
              value={pageSize}
              onChange={e => {
                setPageIndex(0);
                setPageSize(Number(e.target.value));
              }}>
              {[10, 20, 30, 40, 50].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  {pageSize} items
                </option>
              ))}
            </select>
          </span>
          <span>
            {pageIndex * pageSize + 1} to {Math.min((pageIndex + 1) * pageSize, data.length)} of {data.length} items
          </span>
          <span className={`${styles['gray-text']} ${styles['pagination-margin']}`}>
            <button onClick={() => gotoPage(0)} className={`${styles['gray-text']}`} disabled={!canPreviousPage}>
              {'<<'}
            </button>
            <button onClick={() => gotoPage(pageIndex - 1)} className={`${styles['gray-text']}`} disabled={!canPreviousPage}>
              {'<'}
            </button>
            {renderPageNumbers()}
            <button onClick={() => gotoPage(pageIndex + 1)} className={`${styles['gray-text']}`} disabled={!canNextPage}>
              {'>'}
            </button>
            <button onClick={() => gotoPage(pageCount - 1)} className={`${styles['gray-text']}`} disabled={!canNextPage}>
              {'>>'}
            </button>
          </span>
        </div>
      </div>
  );
};

export default TableWithPagination;
